import { ControlValueAccessor } from '@angular/forms';

/**
 * Classe base abstrata que implementa ControlValueAccessor
 * para evitar duplicação de código em componentes de formulário.
 *
 * Como usar:
 * 1. Estenda esta classe no seu componente
 * 2. Implemente o método abstrato `updateValue()`
 * 3. Configure o provider NG_VALUE_ACCESSOR no @Component
 *
 * @example
 * ```typescript
 * @Component({
 *   selector: 'nome-seletor-componente',
 *   providers: [
 *     {
 *       provide: NG_VALUE_ACCESSOR,
 *       useExisting: forwardRef(() => SeuComponente),
 *       multi: true
 *     }
 *   ]
 * })
 * export class SeuComponente extends CustomControlValueAccessor<string> {
 *
 *   protected updateValue(newValue: string): void {
 *     // Implementar a lógica específica do componente
 *     this.internalValue = newValue;
 *   }
 * }
 * ```
 */
export abstract class CustomControlValueAccessor<T = any> implements ControlValueAccessor {

  protected _value: T | null = null;
  protected _disabled = false;
  protected _disabledPorSettings = false;

  // Callbacks do ControlValueAccessor com proteção contra undefined
  protected onChange = (value: T | null) => {};
  protected onTouched = () => {};

  /**
   * Getter para o valor atual
   */
  get value(): T | null {
    return this._value;
  }

  /**
   * Setter para o valor - chama updateValue() para permitir customização
   */
  set value(newValue: T | null) {
    if (this._value === newValue) {
return;
}
    this._value = newValue;
    this.updateValue(newValue);
    this.onChange(newValue);
  }

  /**
   * Getter para o estado disabled
   */
  get disabled(): boolean {
    return this._disabled || this._disabledPorSettings;
  }

  /**
   * Método abstrato que deve ser implementado pelos componentes filhos
   * para definir como o valor será aplicado ao componente específico
   */
  protected abstract updateValue(value: T | null): void;

  // ===== IMPLEMENTAÇÃO DO ControlValueAccessor =====

  /**
   * Método chamado pelo Angular quando o valor do FormControl muda
   */
  writeValue(value: T | null): void {
    this._value = value ?? null;
    this.updateValue(value ?? null);
  }

  /**
   * Registra a função que será chamada quando o valor mudar
   */
  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn || (() => {});
  }

  /**
   * Registra a função que será chamada quando o componente for "tocado"
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn || (() => {});
  }

  /**
   * Habilita/desabilita o componente
   */
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.onDisabledStateChange(this.disabled);
  }

  // ===== MÉTODOS UTILITÁRIOS =====

  /**
   * Método para marcar o componente como "tocado"
   * Deve ser chamado nos eventos de blur, click fora, etc.
   */
  protected markAsTouched(): void {
    this.onTouched();
  }

  /**
   * Método para emitir mudança de valor
   * Use este método quando o valor mudar internamente no componente
   */
  protected emitChange(newValue: T | null): void {
    if (this._value !== newValue) {
      this._value = newValue;
      this.updateValue(newValue);
      this.onChange(newValue);
    }
  }

  protected setDisabledPorSettings(isDisabled: boolean): void {
    if (this._disabledPorSettings === isDisabled) {
      return;
    }
    this._disabledPorSettings = isDisabled;
    this.onDisabledStateChange(this.disabled);
  }

  /**
   * Método chamado quando o estado disabled muda
   * Override este método se precisar de lógica customizada
   */
  protected onDisabledStateChange(isDisabled: boolean): void {
    // Implementação padrão vazia - override se necessário
  }

  /**
   * Verifica se o componente tem um valor válido
   */
  protected hasValue(): boolean {
    return this._value !== null && this._value !== undefined;
  }

  /**
   * Limpa o valor do componente
   */
  protected clearValue(): void {
    this.emitChange(null);
  }
}
